// HTTP fetchers built on a single shared core (apiFetch).
//
// Every non-2xx response becomes an ApiError via buildApiErrorMessage's
// 6-step extraction. Network failures and aborts become ApiError with
// status=0. All four fetchers accept { signal, timeoutMs } in a trailing
// options object.

import { saveAs } from "file-saver";

import { API_SERVER } from "./config";
import { ApiError } from "./api-error";

const DEFAULT_JSON_TIMEOUT_MS = 30000;

// Internal: ApiError for cases where fetch() rejected (no HTTP response).
// Caller abort, timeout, and network failure all share status=0; only the
// message differs.
function networkApiError(path, method, message) {
    return new ApiError({
        status: 0, message, code: null, body: null, endpoint: path, method,
    });
}

// Internal: ApiError for blob/saveAs failures during fetcherFile.
function fileFailureApiError(response, label, err, path, method) {
    return new ApiError({
        status: response.status,
        message: err && err.message ? `${label}: ${err.message}` : label,
        code: null,
        body: null,
        endpoint: path,
        method: method || "GET",
    });
}

// Internal: link multiple AbortSignals into a single controller.
// Avoids relying on AbortSignal.any() which has uneven runtime support.
function linkSignals(signals) {
    const controller = new AbortController();
    const onAbort = (reason) => {
        if (!controller.signal.aborted) {
            controller.abort(reason);
        }
    };
    for (const signal of signals) {
        if (!signal) continue;
        if (signal.aborted) {
            onAbort(signal.reason);
            break;
        }
        signal.addEventListener(
            "abort",
            () => onAbort(signal.reason),
            { once: true },
        );
    }
    return controller;
}

// Internal: extract message/code/body from a non-2xx response.
// Implements the 6-step algorithm from the spec.
async function buildApiErrorMessage(response) {
    const status = response.status;
    const statusText = response.statusText;

    let rawText = "";
    try {
        rawText = await response.text();
    } catch {
        // Body already consumed or unreadable; treat as empty.
        rawText = "";
    }

    // Try to parse the body as JSON.
    let parsedBody = null;
    if (rawText) {
        try {
            parsedBody = JSON.parse(rawText);
        } catch {
            parsedBody = null;
        }
    }

    // Steps 1-4 require a JSON object body.
    if (
        parsedBody !== null &&
        typeof parsedBody === "object" &&
        !Array.isArray(parsedBody)
    ) {
        // Step 1: body has `message` field.
        if (Object.prototype.hasOwnProperty.call(parsedBody, "message")) {
            const message =
                `${parsedBody.error || ""} ${parsedBody.message}`.trim();
            return {
                message,
                code:
                    parsedBody.error !== undefined && parsedBody.error !== null
                        ? parsedBody.error
                        : null,
                body: parsedBody,
            };
        }

        // Step 2 + 3: body has `error` field (with or without success: false).
        if (Object.prototype.hasOwnProperty.call(parsedBody, "error")) {
            return {
                message: String(parsedBody.error),
                code: parsedBody.error,
                body: parsedBody,
            };
        }

        // Step 4: body has `success: false` only.
        if (parsedBody.success === false) {
            return {
                message: `HTTP ${status} ${statusText}`.trim(),
                code: null,
                body: parsedBody,
            };
        }
    }

    // Step 6: Catalyst HTML page special case.
    if (
        rawText &&
        parsedBody === null &&
        rawText.includes("Catalyst") &&
        rawText.includes("Please come back later")
    ) {
        return {
            message: "Server error occurred. Please try again.",
            code: null,
            body: null,
        };
    }

    // Step 5: empty body or non-JSON.
    return {
        message:
            `HTTP ${status} ${statusText || "Server error"}`.trim(),
        code: null,
        body: null,
    };
}

// Internal: parse a 2xx response body as JSON. Returns null for 204 or
// truly empty bodies. Throws ApiError if the body is non-JSON (defensive;
// shouldn't happen given the backend always returns JSON for 2xx).
async function parseSuccessBody(response, endpoint, method) {
    if (response.status === 204) return null;

    let rawText = "";
    try {
        rawText = await response.text();
    } catch {
        rawText = "";
    }

    if (!rawText) return null;

    // Be tolerant: try JSON.parse first regardless of Content-Type, since
    // some 2xx responses may have unusual content-type headers.
    try {
        return JSON.parse(rawText);
    } catch {
        // fall through
    }

    // JSON parse failed; surface this loudly as a contract violation.
    throw new ApiError({
        status: response.status,
        message: "Expected JSON response",
        code: null,
        body: null,
        endpoint,
        method,
    });
}

// Core fetcher. Returns the Response on 2xx; throws ApiError otherwise.
// Network failures and aborts also throw ApiError (status=0).
//
// options: { method, headers, body, signal, timeoutMs }
export async function apiFetch(path, options = {}) {
    const {
        method = "GET",
        headers,
        body,
        signal,
        timeoutMs,
    } = options;

    const url = API_SERVER + path;

    let finalSignal = signal;
    let timeoutController;
    let timeoutId;

    if (timeoutMs && timeoutMs > 0) {
        timeoutController = new AbortController();
        const timeoutReason = new Error(
            `Request timed out after ${timeoutMs}ms`,
        );
        timeoutReason.name = "TimeoutError";
        timeoutId = setTimeout(() => {
            timeoutController.abort(timeoutReason);
        }, timeoutMs);

        if (signal) {
            const linked = linkSignals([signal, timeoutController.signal]);
            finalSignal = linked.signal;
        } else {
            finalSignal = timeoutController.signal;
        }
    }

    const fetchOptions = {
        method,
        credentials: "include",
    };
    if (headers !== undefined) fetchOptions.headers = headers;
    if (body !== undefined) fetchOptions.body = body;
    if (finalSignal !== undefined) fetchOptions.signal = finalSignal;

    let response;
    try {
        response = await fetch(url, fetchOptions);
    } catch (err) {
        // Clean up the timeout timer if it hasn't fired yet.
        if (timeoutId) clearTimeout(timeoutId);

        if (signal && signal.aborted) {
            throw networkApiError(path, method, "Request aborted");
        }
        if (timeoutController && timeoutController.signal.aborted) {
            throw networkApiError(path, method, "Request timed out");
        }
        // Genuine network failure.
        throw networkApiError(path, method, "Network error");
    }

    // Clean up the timeout timer once we have a response.
    if (timeoutId) clearTimeout(timeoutId);

    if (!response.ok) {
        const { message, code, body: parsedBody } =
            await buildApiErrorMessage(response);
        throw new ApiError({
            status: response.status,
            message,
            code,
            body: parsedBody,
            endpoint: path,
            method,
        });
    }

    return response;
}

// GET/DELETE fetcher. Returns parsed JSON body or null (for 204).
// Null path short-circuits to undefined to support SWR conditional fetches.
export async function fetcherSimple(path, method, options = {}) {
    if (!path) return undefined;

    const response = await apiFetch(path, {
        method,
        headers: options.headers,
        signal: options.signal,
        timeoutMs:
            options.timeoutMs !== undefined
                ? options.timeoutMs
                : DEFAULT_JSON_TIMEOUT_MS,
    });

    return parseSuccessBody(response, path, method || "GET");
}

// POST/PUT fetcher with JSON body. Returns parsed JSON body or null.
export async function fetcherFull(path, method, body, options = {}) {
    const fetchOptions = {
        method,
        headers: {
            "Content-Type": "application/json",
            ...(options.headers || {}),
        },
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: options.signal,
        timeoutMs:
            options.timeoutMs !== undefined
                ? options.timeoutMs
                : DEFAULT_JSON_TIMEOUT_MS,
    };

    const response = await apiFetch(path, fetchOptions);
    return parseSuccessBody(response, path, method || "POST");
}

// File download fetcher. Awaits blob() then saveAs(). Returns true on
// success. No default timeout (large files on slow links are legit).
// Null path short-circuits to undefined to support SWR conditional fetches.
export async function fetcherFile(path, method, filename, options = {}) {
    if (!path) return undefined;

    const response = await apiFetch(path, {
        method,
        headers: options.headers,
        signal: options.signal,
        timeoutMs: options.timeoutMs,
    });

    let blob;
    try {
        blob = await response.blob();
    } catch (err) {
        throw fileFailureApiError(response, "Failed to read file", err, path, method);
    }

    // file-saver's saveAs is sync in its public API but we await defensively
    // in case the underlying implementation ever returns a promise.
    try {
        await saveAs(blob, filename);
    } catch (err) {
        throw fileFailureApiError(response, "Failed to save file", err, path, method);
    }

    return true;
}

// FormData upload fetcher. Returns parsed JSON body or null (e.g., empty 204).
// No default timeout (uploads can be large). Callers that previously read the
// return value as a Response object must use the parsed body directly.
export async function fetcherFileUpload(path, method, field, file, options = {}) {
    const formData = new FormData();
    formData.append(field, file);

    const response = await apiFetch(path, {
        method,
        body: formData,
        headers: options.headers,
        signal: options.signal,
        timeoutMs: options.timeoutMs,
    });
    return parseSuccessBody(response, path, method || "POST");
}
